/**
 * F20 (Wave 2D) — admin payment-dispute detail page.
 *
 * Route: `/payment-disputes/:id`
 * Permission: `payment_disputes:read` for the page render; the Resolve CTA is
 * additionally gated on `payment_disputes:resolve` (super_admin + support
 * only; finance sees the read-only view).
 *
 * Composition:
 *   - Booking summary Card + PaymentStatusChip
 *   - PaymentMarksHistoryPanel (latest 5 marks embedded on `BookingDetail`;
 *     full paginated history is a follow-up — F20 ships the recent slice)
 *   - Dispute Card (filer + reason + payment_status_at_filing + filed_at)
 *   - DisputeHistoryPanel (historic resolved/withdrawn rows for the booking)
 *   - ResolveDisputeDialog trigger Button (gated by permission)
 *
 * The Resolve dialog posts to `POST /admin/payment-disputes/:id/resolve` which
 * triggers F17 force-transition + F23 commission reversal hook server-side
 * for the `customer_correct` outcome (Decision 7 + Decision 16).
 */
import { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  Card,
  Chip,
  CurrencyDisplay,
  EmptyState,
  Skeleton,
  StatusChip,
  type StatusChipStatus,
  useToast,
} from '@bravemove/ui-web';
import type { PaymentDisputeStatus, ResolveDisputeInput } from '@bravemove/types';
import { PaymentStatusChip } from '../../components/PaymentStatusChip';
import { PaymentMarksHistoryPanel } from '../../components/PaymentMarksHistoryPanel';
import { DisputeHistoryPanel } from '../../components/DisputeHistoryPanel';
import { ResolveDisputeDialog } from '../../components/ResolveDisputeDialog';
import {
  adminPaymentDisputesApi,
  type PaymentDisputeDetailResponse,
} from '../../api/paymentDisputes';
import { useAuth } from '../../auth/useAuth';

const STATUS_CHIP_MAP: Record<PaymentDisputeStatus, StatusChipStatus> = {
  open: 'in_review',
  resolved_customer_correct: 'approved',
  resolved_venue_correct: 'approved',
  withdrawn: 'pending',
};

function formatLocalTime(iso: string | null, tz: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      timeZone: tz,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export function PaymentDisputeDetailPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ readonly id: string }>();
  const auth = useAuth();
  const toast = useToast();

  const [data, setData] = useState<PaymentDisputeDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);

  const refresh = useCallback(async (disputeId: string) => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminPaymentDisputesApi.detail(disputeId);
      if (resp === null) {
        setNotFound(true);
      } else {
        setData(resp);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!id) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh(id);
  }, [id, refresh]);

  if (!id) return <Navigate to="/payment-disputes" replace />;

  const canResolve = auth.adminUser?.permissions.includes('payment_disputes:resolve') ?? false;

  const handleResolve = async (input: ResolveDisputeInput) => {
    if (!data) return;
    try {
      // The resolve endpoint returns the BARE updated dispute row — merge it
      // into the detail envelope instead of replacing it (BP-01: replacing
      // crashed the page because `booking`/`bookingDisputeHistory` vanished).
      const resolved = await adminPaymentDisputesApi.resolve(data.dispute.id, input);
      setData((prev) => (prev ? { ...prev, dispute: resolved } : prev));
      toast.push({
        tone: 'success',
        title: t('payment:dialog.resolve_dispute.success', {
          defaultValue: 'Dispute resolved',
        }),
      });
    } catch {
      toast.push({
        tone: 'danger',
        title: t('payment:dialog.resolve_dispute.error', {
          defaultValue: 'Could not resolve dispute',
        }),
      });
      throw new Error('RESOLVE_FAILED');
    }
  };

  if (loading) {
    return (
      <div className="max-w-3xl flex flex-col gap-4" data-testid="payment-dispute-loading">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // BP-13 — the load-error branch must run BEFORE the not-found guard: a
  // non-404 failure leaves `data` null, and checking notFound/!data first
  // rendered the misleading "not found" EmptyState on every 500/network error.
  if (error) {
    return (
      <div className="max-w-3xl">
        <Banner
          tone="danger"
          title={t('payment:detail.load_error', { defaultValue: 'Load error' })}
        >
          {error}
        </Banner>
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="max-w-3xl">
        <EmptyState
          title={t('payment:detail.not_found', { defaultValue: 'Dispute not found' })}
          action={
            <Button variant="ghost" onClick={() => navigate('/payment-disputes')}>
              {t('payment:detail.back', { defaultValue: 'Back to queue' })}
            </Button>
          }
        />
      </div>
    );
  }

  const { dispute, booking, filer, bookingDisputeHistory } = data;
  const status = dispute.status;
  const statusChipStatus = STATUS_CHIP_MAP[status] ?? 'pending';

  return (
    <div className="max-w-3xl flex flex-col gap-4" data-testid="payment-dispute-detail-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex flex-col gap-1">
          <button
            type="button"
            className="text-[12px] text-accent hover:underline self-start focus-visible:outline-none focus-visible:ring-[2px] focus-visible:ring-accent/40 rounded"
            onClick={() => navigate('/payment-disputes')}
          >
            {t('payment:detail.back', { defaultValue: 'Back to queue' })}
          </button>
          <h1 className="text-h1 font-bold text-ink">
            {t('payment:detail.title', { defaultValue: 'Payment dispute' })}
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusChip
            size="sm"
            status={statusChipStatus}
            pulse={status === 'open'}
            label={t(`payment:queue.status_${status}`, {
              defaultValue:
                status === 'open'
                  ? 'Open'
                  : status === 'resolved_customer_correct'
                    ? 'Customer correct'
                    : status === 'resolved_venue_correct'
                      ? 'Venue correct'
                      : 'Withdrawn',
            })}
          />
          {booking ? <PaymentStatusChip state={booking.paymentStatus} /> : null}
        </div>
      </header>

      {/* Booking summary */}
      <Card>
        <div className="flex flex-col gap-3" data-testid="dispute-booking-summary">
          <h2 className="text-h2 font-bold text-ink">
            {t('payment:detail.booking_title', { defaultValue: 'Booking' })}
          </h2>
          {booking ? (
            <dl className="grid grid-cols-[140px_1fr] gap-y-2 gap-x-3 text-[12.5px]">
              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.booking_id', { defaultValue: 'Booking id' })}
              </dt>
              <dd className="text-ink font-mono tabular-nums">{booking.id}</dd>

              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.venue', { defaultValue: 'Venue' })}
              </dt>
              <dd className="text-ink">{booking.venueName}</dd>

              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.court', { defaultValue: 'Court' })}
              </dt>
              <dd className="text-ink">{booking.subVenueName}</dd>

              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.sport', { defaultValue: 'Sport' })}
              </dt>
              <dd className="text-ink">{booking.sportName}</dd>

              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.time', { defaultValue: 'Time' })}
              </dt>
              <dd className="text-ink">
                {formatLocalTime(booking.startAt, booking.venueTimezone)} —{' '}
                {formatLocalTime(booking.endAt, booking.venueTimezone)}
              </dd>

              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.price', { defaultValue: 'Price' })}
              </dt>
              <dd className="text-ink">
                <CurrencyDisplay
                  amount={booking.price.priceAmount}
                  currency={booking.price.priceCurrency}
                />
              </dd>

              <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.booker', { defaultValue: 'Booker' })}
              </dt>
              <dd className="text-ink">
                {booking.hostDisplayName ??
                  t('payment:detail.anonymous_booker', {
                    defaultValue: 'Anonymous booker',
                  })}
              </dd>
            </dl>
          ) : (
            <EmptyState
              title={t('payment:detail.booking_unavailable', {
                defaultValue: 'Booking details unavailable',
              })}
            />
          )}
        </div>
      </Card>

      {/* Payment marks (recent slice from BookingDetail) */}
      <PaymentMarksHistoryPanel
        marks={booking?.recentPaymentMarks ?? []}
        title={t('payment:detail.payment_history', { defaultValue: 'Payment history' })}
      />

      {/* Dispute card — filer + reason + snapshot */}
      <Card>
        <div className="flex flex-col gap-3" data-testid="dispute-card">
          <h2 className="text-h2 font-bold text-ink">
            {t('payment:detail.dispute_title', { defaultValue: 'Dispute' })}
          </h2>

          <dl className="grid grid-cols-[140px_1fr] gap-y-2 gap-x-3 text-[12.5px]">
            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('payment:detail.filed_by', { defaultValue: 'Filed by' })}
            </dt>
            <dd className="text-ink">
              {filer?.displayName ?? t('payment:detail.unknown_filer', { defaultValue: 'Unknown' })}
              {filer?.email ? (
                <span className="ms-2 font-mono text-[11px] text-ink-3">{filer.email}</span>
              ) : null}
            </dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('payment:detail.filed_at', { defaultValue: 'Filed at' })}
            </dt>
            <dd className="text-ink-2 text-[11.5px] font-mono tabular-nums">{dispute.createdAt}</dd>

            <dt className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('payment:detail.status_at_filing', {
                defaultValue: 'Status at filing',
              })}
            </dt>
            <dd className="text-ink">
              <Chip>{dispute.paymentStatusAtFiling}</Chip>
            </dd>
          </dl>

          <div className="border-t border-hairline pt-3">
            <span className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
              {t('payment:detail.reason', { defaultValue: 'Reason' })}
            </span>
            <p className="mt-1 text-[13px] text-ink leading-relaxed whitespace-pre-wrap">
              {dispute.reason}
            </p>
          </div>

          {dispute.resolutionNotes ? (
            <div className="border-t border-hairline pt-3">
              <span className="text-ink-3 font-mono uppercase tracking-wider text-[10.5px]">
                {t('payment:detail.resolution_notes', {
                  defaultValue: 'Resolution notes',
                })}
              </span>
              <p className="mt-1 text-[13px] text-ink-2 leading-relaxed whitespace-pre-wrap">
                {dispute.resolutionNotes}
              </p>
            </div>
          ) : null}

          {status === 'open' ? (
            <div className="border-t border-hairline pt-3 flex items-center justify-end gap-2 flex-wrap">
              {canResolve ? (
                <Button
                  variant="primary"
                  onClick={() => setResolveOpen(true)}
                  data-testid="dispute-resolve-cta"
                >
                  {t('payment:action.resolve_dispute', { defaultValue: 'Resolve dispute' })}
                </Button>
              ) : (
                <div data-testid="dispute-readonly-banner">
                  <Banner tone="info">
                    {t('payment:detail.readonly_finance', {
                      defaultValue:
                        'Finance role is read-only on disputes; ask a super_admin or support to resolve.',
                    })}
                  </Banner>
                </div>
              )}
            </div>
          ) : null}
        </div>
      </Card>

      {bookingDisputeHistory.length > 0 ? (
        <DisputeHistoryPanel
          disputes={bookingDisputeHistory}
          title={t('payment:detail.previous_disputes', {
            defaultValue: 'Previous disputes for this booking',
          })}
        />
      ) : null}

      <ResolveDisputeDialog
        dispute={dispute}
        open={resolveOpen}
        onClose={() => setResolveOpen(false)}
        onConfirm={handleResolve}
      />
    </div>
  );
}

export default PaymentDisputeDetailPage;
